import { Component } from '@theme/component';

/** Native request form with an explicitly illustrated concept, never a recolored product photo. */
class DecBuilder extends Component {
  controller;
  step = 0;
  side = 'Front';
  seed = 42;
  center = { x: 245, y: 245 };
  history = [];
  lastState;
  motion;

  connectedCallback() {
    super.connectedCallback();
    this.bind();
    const reference = this.querySelector('[ref="reference"]');
    const handle = new URL(window.location.href).searchParams.get('reference');
    if (reference instanceof HTMLInputElement && handle && /^[a-z0-9-]{1,150}$/.test(handle) && !reference.value) {
      reference.value = new URL('/products/' + handle, window.location.origin).href;
    }
    this.refresh();
    this.lastState = this.snapshot();
  }
  updatedCallback() { super.updatedCallback(); this.bind(); this.refresh(); this.lastState = this.snapshot(); }
  disconnectedCallback() { this.controller?.abort(); this.motion?.cancel(); super.disconnectedCallback(); }

  bind() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.addEventListener('input', event => {
      if (!(event.target instanceof HTMLInputElement)) return;
      this.remember(); this.refresh(); this.lastState = this.snapshot();
    }, options);
    this.addEventListener('change', event => {
      if (event.target instanceof HTMLInputElement && event.target.dataset.summary === 'placement' && ['Front','Back'].includes(event.target.value)) this.side = event.target.value;
      this.remember(); this.refresh(); this.lastState = this.snapshot(); this.animate();
    }, options);
    this.addEventListener('invalid', () => {
      const first = this.querySelector('form input:invalid, form textarea:invalid');
      const panel = first?.closest('[data-panel]');
      if (panel instanceof HTMLElement) this.showStep(Number(panel.dataset.panel), false);
    }, { ...options, capture: true });
    this.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('button');
      if (!(button instanceof HTMLButtonElement)) return;
      if (button.hasAttribute('data-step')) this.showStep(Number(button.dataset.step), true);
      if (button.hasAttribute('data-back')) this.showStep(this.step - 1, true);
      if (button.hasAttribute('data-next')) {
        const invalid = this.querySelector(`[data-panel="${this.step}"] input:invalid, [data-panel="${this.step}"] textarea:invalid`);
        if (invalid instanceof HTMLInputElement || invalid instanceof HTMLTextAreaElement) invalid.reportValidity();
        else this.showStep(this.step + 1, true);
      }
      if (button.hasAttribute('data-face')) { this.side = button.dataset.face || 'Front'; this.refresh(); this.animate(); }
      if (button.hasAttribute('data-undo')) this.undo();
      if (button.hasAttribute('data-preset')) this.edit(() => { this.setColors(button.dataset.preset || '');
        if (button.dataset.exampleStyle) for (const field of this.querySelectorAll('[data-summary="dye"]')) field.checked = field.value === button.dataset.exampleStyle;
      });
      if (button.hasAttribute('data-color')) this.edit(() => {
        const field = this.querySelector('[data-summary="colors"]');
        if (!(field instanceof HTMLInputElement)) return;
        const colors = field.value.split(',').map(value => value.trim()).filter(Boolean);
        const color = button.dataset.color || '';
        const found = colors.findIndex(value => value.toLowerCase() === color.toLowerCase());
        if (found === -1) colors.push(color); else colors.splice(found, 1);
        field.value = colors.join(', ');
      });
      if (button.hasAttribute('data-shuffle')) this.edit(() => {
        const palettes = ['Turquoise, Blue, White', 'Green, Brown, Black', 'Pink, Red, Orange', 'Purple, Blue, Pink', 'Black, Grey, White', 'Yellow, Orange, Red'];
        const current = this.fieldValue('colors');
        const choices = palettes.filter(palette => palette !== current);
        this.setColors(choices[Math.floor(Math.random() * choices.length)]);
      });
    }, options);
    for (const element of this.querySelectorAll('.dec-build__steps,.dec-build__palette-tools,.dec-build__pager,[data-studio-tools]')) {
      if (element instanceof HTMLElement) element.hidden = false;
    }
    this.dataset.enhanced = 'true';
    this.showStep(this.querySelector('[role="alert"],.dec-build__success') ? 3 : this.step, false);
  }

  fieldValue(key) {
    const input = this.querySelector(`[data-summary="${key}"]:checked`) || this.querySelector(`[data-summary="${key}"]`);
    return input instanceof HTMLInputElement ? input.value : '';
  }
  setColors(colors) { const field = this.querySelector('[data-summary="colors"]'); if (field instanceof HTMLInputElement) field.value = colors; }
  snapshot() {
    return { fields: [...this.querySelectorAll('[data-summary]')].filter(el => el instanceof HTMLInputElement && (el.type !== 'radio' || el.checked)).map(el => [el.dataset.summary, el.value]), seed:this.seed, center:{...this.center} };
  }
  remember() {
    if (this.lastState && JSON.stringify(this.lastState) !== JSON.stringify(this.snapshot())) {
      this.history.push(this.lastState); if (this.history.length > 30) this.history.shift();
    }
  }
  edit(action) { const before = this.snapshot(); action(); this.lastState = before; this.remember(); this.refresh(); this.lastState = this.snapshot(); this.animate(); }
  undo() {
    const state = this.history.pop(); if (!state) return;
    for (const [key, value] of state.fields) for (const field of this.querySelectorAll(`[data-summary="${key}"]`)) {
      if (!(field instanceof HTMLInputElement)) continue;
      if (field.type === 'radio') field.checked = field.value === value; else field.value = value;
    }
    // A size that was previously unselected must become unselected again.
    if (!state.fields.some(([key]) => key === 'size')) for (const field of this.querySelectorAll('[data-summary="size"]')) if (field instanceof HTMLInputElement) field.checked = false;
    this.seed = state.seed; this.center = {...state.center}; this.lastState = this.snapshot(); this.refresh(); this.animate();
  }
  showStep(next, focus) {
    this.step = Math.max(0, Math.min(3, next));
    for (const panel of this.querySelectorAll('[data-panel]')) if (panel instanceof HTMLElement) panel.hidden = Number(panel.dataset.panel) !== this.step;
    for (const button of this.querySelectorAll('[data-step]')) {
      if (Number(button.getAttribute('data-step')) === this.step) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
    }
    const back = this.querySelector('[data-back]'); const forward = this.querySelector('[data-next]');
    if (back instanceof HTMLElement) back.hidden = this.step === 0;
    if (forward instanceof HTMLElement) forward.hidden = this.step === 3;
    if (focus) {
      const heading = this.querySelector(`[data-panel="${this.step}"] h2`);
      if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
      this.querySelector('.dec-build__steps')?.scrollIntoView({ block:'start', behavior:'instant' });
    }
  }

  refresh() {
    this.updateSummary(); this.draw(); this.updateExamples();
    const undo = this.querySelector('[data-undo]'); if (undo instanceof HTMLButtonElement) undo.disabled = !this.history.length;
    for (const button of this.querySelectorAll('[data-face]')) button.setAttribute('aria-pressed', String(button.getAttribute('data-face') === this.side));
    const status = this.querySelector('[data-studio-status]'); if (status) status.textContent = `${this.side}: ${this.fieldValue('garment')}, ${this.fieldValue('dye')}, ${this.fieldValue('colors') || 'no swatches selected'}.`;
    const sketch = this.querySelector('[data-sketch-state]');
    if (sketch instanceof HTMLInputElement) sketch.value = this.dataset.mockupRequest;
  }
  updateSummary() {
    const list = this.querySelector('[ref="summaryList"]'); const summary = this.querySelector('[ref="summary"]');
    if (!(list instanceof HTMLElement) || !(summary instanceof HTMLElement)) return;
    const fragment = document.createDocumentFragment();
    for (const field of this.querySelectorAll('[data-summary]')) {
      if (!(field instanceof HTMLInputElement) || (field.type === 'radio' && !field.checked) || !field.value.trim()) continue;
      const term = document.createElement('dt'); const detail = document.createElement('dd');
      term.textContent = field.dataset.label || ''; detail.textContent = field.type === 'radio' ? (field.nextElementSibling?.querySelector('span')?.textContent || field.value) : field.value;
      fragment.append(term, detail);
    }
    list.replaceChildren(fragment); summary.hidden = false;
    const review = this.querySelector('[data-review]'); const copy = list.cloneNode(true);
    if (copy instanceof HTMLElement) { copy.removeAttribute('ref'); review?.replaceChildren(copy); }
    const chosen = this.fieldValue('colors').toLowerCase().split(',').map(value => value.trim());
    const strip = document.createDocumentFragment();
    for (const button of this.querySelectorAll('[data-color]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const active = chosen.includes((button.dataset.color || '').toLowerCase()); button.setAttribute('aria-pressed', String(active));
      if (active) { const span = document.createElement('span'); span.style.setProperty('--swatch', button.dataset.colorHex || 'transparent'); strip.append(span); }
    }
    this.querySelector('[data-palette-strip]')?.replaceChildren(strip);
    const label = this.querySelector('[data-mix-label]'); if (label) label.textContent = this.fieldValue('colors');
  }

  draw() {
    const group = this.querySelector('[data-ink]'); if (!group) return;
    const hoodie = this.fieldValue('garment') === 'Hoodie';
    const tee = 'M160 65 75 102 24 185 103 230 139 180 128 440Q250 455 372 440L361 180 397 230 476 185 425 102 340 65 300 49Q250 90 200 49Z';
    const hood = 'M185 100C153 0 347 0 315 100L389 128 451 403 391 426 352 266 366 455 134 455 148 266 109 426 49 403 111 128Z';
    for (const path of this.querySelectorAll('[data-shape],[data-outline]')) path.setAttribute('d', hoodie ? hood : tee);
    this.querySelector('[data-seams]')?.setAttribute('d', hoodie ? (this.side === 'Front' ? 'M185 100Q250 160 315 100M211 136 211 230M289 136 289 230M204 333H296L320 409H180Z' : 'M185 100Q250 210 315 100M136 435H364') : (this.side === 'Front' ? 'M200 49Q250 110 300 49M139 180 152 125M361 180 348 125M130 424Q250 439 370 424' : 'M200 49Q250 67 300 49M139 180 152 125M361 180 348 125M130 424Q250 439 370 424'));
    const {colors}=this.palette();
    this.drawDye(group,colors); this.drawPrint(colors);
  }
  svg(tag, attrs = {}) {
    const node = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, String(value));
    return node;
  }
  palette() {
    const swatches = [...this.querySelectorAll('[data-color]')];
    const names = this.fieldValue('colors').split(',').map(s => s.trim()).filter(Boolean);
    const colors = []; let unknown = false;
    for (const name of names) {
      const swatch = swatches.find(b => b.dataset.color.toLowerCase() === name.toLowerCase());
      if (swatch) colors.push(swatch.dataset.colorHex);
      else if (name.toLowerCase() === 'navy') colors.push('#202637');
      else if (/^(#[\da-f]{3,8}|[a-z]+)$/i.test(name) && CSS.supports('color', name) && !/^(transparent|inherit|initial|unset|currentcolor)$/i.test(name)) colors.push(name);
      else unknown = true;
    }
    return { colors: colors.length ? colors : ['#f3f2ed'], unknown };
  }
  drawDye(group, colors) {
    const style = this.fieldValue('dye');
    const key = [style, ...colors].join('|');
    if (group.dataset.texture === key) return;
    this.dyeTextures ||= new Map();
    let texture = this.dyeTextures.get(key);
    if (!texture) {
      const canvas = document.createElement('canvas');
      canvas.width = 500; canvas.height = 490;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // All pixels below are a generated illustration. No product photo is recolored.
      const palette = colors.map(color => {
        ctx.fillStyle = color; ctx.fillRect(0, 0, 1, 1);
        return [...ctx.getImageData(0, 0, 1, 1).data].slice(0, 3);
      });
      const pixels = ctx.createImageData(500, 490);
      const clamp = v => Math.max(0, Math.min(1, v));
      const smooth = v => { v = clamp(v); return v * v * (3 - 2 * v); };
      const hash = (x, y) => {
        let h = Math.imul(x, 374761393) + Math.imul(y, 668265263) + 1447;
        h = Math.imul(h ^ (h >>> 13), 1274126177);
        return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
      };
      const noise = (x, y) => {
        const ix = Math.floor(x), iy = Math.floor(y), fx = x - ix, fy = y - iy;
        const fade = t => t * t * t * (t * (t * 6 - 15) + 10);
        const gradient = (gx, gy, dx, dy) => {
          const h = Math.floor(hash(gx, gy) * 8);
          return ((h & 1) ? -dx : dx) + ((h & 2) ? -dy : dy) * ((h & 4) ? .5 : 1);
        };
        const u = fade(fx), v = fade(fy);
        const a = gradient(ix, iy, fx, fy), b = gradient(ix + 1, iy, fx - 1, fy);
        const c = gradient(ix, iy + 1, fx, fy - 1), d = gradient(ix + 1, iy + 1, fx - 1, fy - 1);
        return clamp(.5 + (a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v) * .7);
      };
      // Cache the cotton and dye-flow fields: changing a color only mixes pigment.
      if (!this.dyeFields) {
        this.dyeFields = new Float32Array(500 * 490 * 6);
        for (let y = 0; y < 490; y++) for (let x = 0; x < 500; x++) {
          const i = (y * 500 + x) * 6;
          this.dyeFields[i] = noise(x * .009 + 8, y * .009 + 13);
          this.dyeFields[i + 1] = noise(x * .035 + 31, y * .035 + 7);
          this.dyeFields[i + 2] = noise(x * .12 + 5, y * .12 + 47);
          this.dyeFields[i + 3] = noise(x * .32, y * .32);
          this.dyeFields[i + 4] = hash(x, y);
          this.dyeFields[i + 5] = noise(x * .004 + 21, y * .004 + 32);
        }
      }
      const count = palette.length;
      const weights = palette.map(rgb => style === 'Geode' && Math.min(...rgb) > 220 ? .12 : 1);
      const totalWeight = weights.reduce((a, b) => a + b, 0);
      for (let y = 0; y < 490; y++) for (let x = 0; x < 500; x++) {
        const p = y * 500 + x, f = p * 6;
        const broad = this.dyeFields[f], medium = this.dyeFields[f + 1], fine = this.dyeFields[f + 2], grain = this.dyeFields[f + 3], fleck = this.dyeFields[f + 4], slow = this.dyeFields[f + 5];
        const dx = x - 218, dy = y - 268, radius = Math.hypot(dx, dy), angle = Math.atan2(dy, dx);
        let phase = 0, resist = 0, pigment = 1;
        if (style === 'Geode') {
          phase = Math.hypot((x - 177) * .85, y - 303) / 14 + (broad - .5) * 6.2 + (medium - .5) * 1.7 + (fine - .5) * .65 + (grain - .5) * .13;
          const vein = Math.abs(Math.sin(phase * Math.PI));
          resist = (1 - smooth(vein / (.12 + fine * .12))) * .62;
          pigment = .76 + medium * .25 + fine * .15;
        } else if (style === 'Spiral') {
          phase = (angle / (2 * Math.PI) * 2 + radius * .007) * count + (medium - .5) * .55 + (fine - .5) * .2;
          const pleat = Math.abs(Math.sin(angle * 34 + radius * .025 + broad * 5 + medium * 5 + fine * 3));
          resist = Math.pow(pleat, 18) * (.05 + fine * .16);
          pigment = .75 + medium * .3 + fine * .12;
        } else if (style !== 'Solid') {
          phase = clamp((broad * .55 + medium * .3 + fine * .15 - .23) * 1.85) * (count - 1);
          resist = Math.pow(clamp((medium + fine * .4 - .5) * 1.4), 2) * .35;
          pigment = .65 + medium * .35;
        }
        let position = ((phase % count) + count) % count / count * totalWeight;
        let index = 0;
        while (index < count - 1 && position >= weights[index]) position -= weights[index++];
        const fraction = position / weights[index];
        const blend = style === 'Solid' ? 0 : style === 'Geode' ? smooth((fraction - .8) / .2) : style === 'Spiral' ? smooth((fraction - .25) / .7) : smooth(fraction);
        const a = palette[index], b = palette[(index + 1) % count];
        // Small pale fibers, uneven absorption and soft folds give the dye a cloth surface.
        const cotton = (fleck - .5) * 6 + (grain - .5) * 5 + (y % 2 ? -.6 : .6);
        const folds = Math.sin(x * .09 + broad * 4 + y * .012) * .018;
        const sideShade = .06 * Math.exp(-Math.pow((x - 146 - y * .015) / 12, 2)) + .05 * Math.exp(-Math.pow((x - 356 + y * .012) / 15, 2));
        const lighting = .93 + slow * .1 + folds - sideShade;
        for (let c = 0; c < 3; c++) {
          let value = a[c] * (1 - blend) + b[c] * blend;
          if (style !== 'Solid') value = 247 - (247 - value) * pigment;
          value = value * (1 - resist) + 244 * resist;
          pixels.data[p * 4 + c] = value * lighting + cotton;
        }
        pixels.data[p * 4 + 3] = 255;
      }
      ctx.putImageData(pixels, 0, 0);
      texture = canvas.toDataURL('image/png');
      if (this.dyeTextures.size >= 8) this.dyeTextures.delete(this.dyeTextures.keys().next().value);
      this.dyeTextures.set(key, texture);
    }
    group.replaceChildren(this.svg('image', {width:500, height:490, href:texture}));
    group.dataset.texture = key;
  }
  drawPrint(colors) {
    const print=this.querySelector('[data-print]'); if(!print)return;
    const graphic=this.fieldValue('graphic'),placement=this.fieldValue('placement');
    const chosen=!['No graphic','Help me choose',''].includes(graphic);
    const tentative=placement==='Help me choose';
    const visible=chosen&&(tentative||placement===this.side||placement==='Front and back');
    const note=this.querySelector('[data-preview-note]');
    const notes=[];
    if(this.palette().unknown)notes.push(this.dataset.unknownColor);
    if(this.fieldValue('dye')==='Solid'&&colors.length>1)notes.push(this.dataset.solidNote);
    if(chosen&&!visible)notes.push(this.dataset.otherSide);
    else if(chosen&&tentative)notes.push(this.dataset.tentativePlacement);
    if(note){note.textContent=notes.join(' ');note.hidden=!notes.length;}
    print.toggleAttribute('hidden',!visible);
    if(!visible)return;
    const art=[...this.querySelectorAll('[data-art]')].find(el=>el.dataset.art===graphic);
    const key=[graphic,this.side,this.fieldValue('garment'),colors.join(',')].join('|');
    if(print.dataset.key===key)return;
    print.dataset.key=key;print.replaceChildren();print.removeAttribute('transform');
    if(!art)return;
    const crop=art.dataset.crop.split(' ').map(Number);
    const w=this.side==='Back'?190:150,h=Math.min(155,w*crop[3]/crop[2]);
    const y=this.fieldValue('garment')==='Hoodie'?180:155;
    const viewport=this.svg('svg',{x:250-w/2,y,width:w,height:h,viewBox:art.dataset.crop,preserveAspectRatio:'xMidYMid meet',overflow:'hidden'});
    const defs=this.svg('defs');
    const id=this.querySelector('[data-ink]').dataset.filter+'-print';
    const filter=this.svg('filter',{id,x:0,y:0,width:1,height:1,'color-interpolation-filters':'sRGB'});
    // Only the print area is sampled from the real photo. Fabric is masked in the mockup alone.
    const slope=Number(art.dataset.slope),intercept=Number(art.dataset.intercept);
    if(slope!==0){
      const hex=colors[0].replace('#','');
      const dark=/^[0-9a-f]{6}$/i.test(hex) && (.2126*parseInt(hex.slice(0,2),16)+.7152*parseInt(hex.slice(2,4),16)+.0722*parseInt(hex.slice(4,6),16))<115;
      const ink=dark?1:.1;
      filter.append(this.svg('feColorMatrix',{type:'matrix',values:`0 0 0 0 ${ink} 0 0 0 0 ${ink} 0 0 0 0 ${ink} ${slope} 0 0 0 ${intercept}`}));
      defs.append(filter);viewport.append(defs);
    }
    viewport.style.opacity='0';
    const photo=this.svg('image',{width:1000,height:art.dataset.height,preserveAspectRatio:'none'});
    photo.addEventListener('load',()=>{viewport.style.opacity='1';},{once:true});
    photo.setAttribute('href',art.dataset.src);
    if(slope!==0)photo.setAttribute('filter',`url(#${id})`);
    viewport.append(photo);print.append(viewport);
  }
  updateExamples() {
    const dye=this.fieldValue('dye');
    for(const example of this.querySelectorAll('[data-example]'))example.hidden=dye!=='Help me choose'&&example.dataset.example!==dye;
  }

  animate() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.motion?.cancel();
    this.motion = this.querySelector('[data-drawing]')?.animate([{opacity:.55,transform:'translateY(3px)'},{opacity:1,transform:'translateY(0)'}],{duration:220,easing:'ease-out'});
  }
}
if (!customElements.get('dec-builder-component')) customElements.define('dec-builder-component', DecBuilder);
